import React, { useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { Select } from '../../../components';
import { domiciliaryScheduleService } from '../../../services';
import { toast } from 'react-hot-toast';

// Add one-off EXTRA availability for a carer on a single date (zone + window +
// note). Creates a green block and, if a zone is chosen, cover capacity there.
// `onStage(payload)` (optional) — when provided (Schedule screen), availability
// is queued as a draft change instead of written immediately via `onSaved`.
const AvailabilityModal = ({ open, carers = [], zones = [], defaultDate, defaultEmployeeId, defaultStart, defaultEnd, onClose, onSaved, onStage }) => {
  const realZones = useMemo(() => zones.filter((z) => z.id != null), [zones]);
  const [zoneId, setZoneId] = useState(realZones[0]?.id || '');
  const [date, setDate] = useState(defaultDate);
  const [employeeId, setEmployeeId] = useState(defaultEmployeeId ? String(defaultEmployeeId) : '');
  const [startTime, setStartTime] = useState(defaultStart || '07:00');
  const [endTime, setEndTime] = useState(defaultEnd || '18:00');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedZone = realZones.find((z) => String(z.id) === String(zoneId));

  const save = async () => {
    if (!employeeId) { toast.error('Pick a staff member'); return; }
    if (endTime <= startTime) { toast.error('End time must be after start time'); return; }
    if (onStage) {
      const name = carers.find((c) => String(c.id) === String(employeeId))?.name || 'Carer';
      onStage({ employee_id: Number(employeeId), name, zone_id: zoneId || null, start: startTime, end: endTime, note });
      toast.success('Availability added to draft');
      onClose();
      return;
    }
    setSaving(true);
    try {
      const res = await domiciliaryScheduleService.createAvailability({
        employee_id: employeeId,
        zone_id: zoneId || null,
        date,
        start_time: startTime,
        end_time: endTime,
        note,
      });
      if (res.success) { toast.success('Availability added'); onSaved(); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error saving availability');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Add availability"
      maxWidth="max-w-xl"
      footer={
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 rounded-lg border border-gray-200 dark:border-gray-700 px-5 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]">Cancel</button>
          <button onClick={save} disabled={saving} className="h-10 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save availability'}
          </button>
        </div>
      }
    >
      <p className="-mt-2 mb-4 text-xs text-gray-400 dark:text-gray-500">
        Add extra availability for one date. If the carer doesn’t normally cover this zone, it also creates cover capacity.
      </p>

      {/* Zone banner */}
      <div className="mb-4 flex items-center gap-2 rounded-lg bg-blue-50/70 px-3 py-2.5 text-sm">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: selectedZone?.color || '#94a3b8' }} />
        <span className="font-semibold text-gray-700 dark:text-gray-200">{selectedZone?.name || 'No zone'}</span>
        <span className="text-gray-400 dark:text-gray-500">· Extra availability</span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Zone</label>
          <Select
            className="w-full"
            value={zoneId}
            onChange={setZoneId}
            placeholder="No zone"
            options={[{ value: '', label: 'No zone' }, ...realZones.map((z) => ({ value: String(z.id), label: z.name }))]}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="h-11 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Staff member</label>
          <Select
            className="w-full"
            value={employeeId}
            onChange={setEmployeeId}
            placeholder="Select…"
            options={carers.map((c) => ({ value: String(c.id), label: c.name }))}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Start time</label>
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
            className="h-11 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">End time</label>
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
            className="h-11 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none" />
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Note (optional)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. sickness cover"
          className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none" />
      </div>

      <p className="mt-3 rounded-lg bg-gray-50 dark:bg-[#20242c] px-3 py-2 text-[11px] text-gray-400 dark:text-gray-500">
        This adds a one-day green availability block for that carer.
      </p>
    </Modal>
  );
};

export default AvailabilityModal;
